import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/context/auth-context";
import { useActiveOrganization } from "@/hooks/organizations";
import type { OrganizationUsageRangeParams } from "@/types/organization-usage";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";

interface UseOrganizationUsageQueryParams extends OrganizationUsageRangeParams {
  queryKey: string;
  endpoint: (organizationId: number) => string;
}

export function useOrganizationUsageQuery<TData>({
  start_date,
  end_date,
  isSubscriptionLoading = false,
  queryKey,
  endpoint,
}: UseOrganizationUsageQueryParams) {
  const userToken = useAuth()?.userToken ?? "";
  const { data: activeOrganization } = useActiveOrganization();
  const organizationId = activeOrganization?.id;

  return useQuery(
    [queryKey, organizationId, start_date, end_date],
    (): Promise<TData> => {
      const url = new URL(endpoint(organizationId!));
      url.searchParams.append("start_date", start_date!);
      url.searchParams.append("end_date", end_date!);
      return fetch(url, {
        method: "GET",
        headers: jsonAuthHeaders(userToken),
      }).then(handleResponse);
    },
    {
      enabled: !!organizationId && !!start_date && !!end_date && !isSubscriptionLoading,
    },
  );
}
